import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Building,
  Mail,
  Handshake,
  FileText,
  Loader2,
  Copy,
  Download,
  History,
  AlertCircle,
  Lock,
  ChevronRight,
  Wand2,
  Heart,
  Share2,
  Save,
  Presentation,
  Eye,
  Printer,
  ExternalLink,
  Palette
} from 'lucide-react';

const GENERATION_TYPES = [
  {
    id: 'sponsorship_proposal',
    name: 'Corporate Sponsorship Proposal',
    description: 'Professional sponsorship package with tiers, ROI metrics, and impact stories',
    icon: Building,
    color: 'bg-blue-100 text-blue-700',
    requiresPartner: true,
    requiresProgram: false,
    requiresGrant: false,
  },
  {
    id: 'email_campaign',
    name: 'Email Campaign',
    description: 'Strategic 5-email sequence with pattern interrupts and compelling storytelling',
    icon: Mail,
    color: 'bg-green-100 text-green-700',
    requiresPartner: true,
    requiresProgram: false,
    requiresGrant: false,
  },
  {
    id: 'partnership_proposal',
    name: 'Partnership Proposal',
    description: 'Comprehensive proposal with executive summary, models, and success metrics',
    icon: Handshake,
    color: 'bg-purple-100 text-purple-700',
    requiresPartner: true,
    requiresProgram: false,
    requiresGrant: false,
  },
  {
    id: 'grant_writeup',
    name: 'Grant Write-up',
    description: 'Funder-ready proposal with statement of need, evaluation plan, and budget narrative',
    icon: FileText,
    color: 'bg-orange-100 text-orange-700',
    requiresPartner: false,
    requiresProgram: true,
    requiresGrant: true,
  },
  {
    id: 'donation_appeal',
    name: 'Donation Appeal',
    description: 'Multi-channel appeal letter with storytelling, dollar handles, and urgency drivers',
    icon: Heart,
    color: 'bg-red-100 text-red-700',
    requiresPartner: false,
    requiresProgram: true,
    requiresGrant: false,
  },
  {
    id: 'social_media_campaign',
    name: 'Social Media Campaign',
    description: 'Full campaign with platform-specific content, hashtags, and content calendar',
    icon: Share2,
    color: 'bg-pink-100 text-pink-700',
    requiresPartner: false,
    requiresProgram: false,
    requiresGrant: false,
  },
  {
    id: 'board_presentation',
    name: 'Board Presentation',
    description: 'Downloadable slide deck with agenda, engagement tips, and virtual meeting best practices',
    icon: FileText,
    color: 'bg-indigo-100 text-indigo-700',
    requiresPartner: false,
    requiresProgram: false,
    requiresGrant: false,
    isPresentation: true,
  },
];

export default function AIGenerator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [partners, setPartners] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [grants, setGrants] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedGrant, setSelectedGrant] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [generatedContent, setGeneratedContent] = useState('');
  const [pptxData, setPptxData] = useState(null);
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [aiUsage, setAiUsage] = useState({ remaining: 0, limit: 0, is_unlimited: false });

  const isPro = user?.subscription_plan === 'pro' || user?.subscription_plan === 'trial';
  const isBasic = user?.subscription_plan === 'basic';
  const isFree = user?.subscription_plan === 'free' || !user?.subscription_plan;
  const hasAIAccess = isPro || isBasic || (isFree && !aiUsage.trial_used);

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchData();
      fetchAIUsage();
    }
  }, [user, navigate]);

  const fetchAIUsage = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAiUsage(data);
      }
    } catch (error) {
      console.error('Error fetching AI usage:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [orgRes, partnersRes, programsRes, grantsRes, genRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organization`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grants`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/generations?limit=10`, { headers }),
      ]);

      if (orgRes.ok) {
        const data = await orgRes.json();
        setOrganization(data);
      }
      if (partnersRes.ok) setPartners(await partnersRes.json());
      if (programsRes.ok) setPrograms(await programsRes.json());
      if (grantsRes.ok) setGrants(await grantsRes.json());
      if (genRes.ok) setGenerations(await genRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedType) {
      toast.error('Please select a content type');
      return;
    }

    const typeConfig = GENERATION_TYPES.find(t => t.id === selectedType);
    
    if (typeConfig.requiresPartner && !selectedPartner) {
      toast.error('Please select a partner for this content type');
      return;
    }
    
    if (typeConfig.requiresProgram && !selectedProgram) {
      toast.error('Please select a program for this content type');
      return;
    }
    
    if (typeConfig.requiresGrant && !selectedGrant) {
      toast.error('Please select a grant for this content type');
      return;
    }

    setGenerating(true);
    setPptxData(null);
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      
      // Use different endpoint for board presentations
      const endpoint = typeConfig.isPresentation 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-presentation`
        : `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          generation_type: selectedType,
          partner_id: selectedPartner || null,
          program_id: selectedProgram || null,
          grant_id: selectedGrant || null,
          additional_context: additionalContext || null,
          tone,
          length,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedContent(data.content);
        
        // If it's a presentation, store the pptx data
        if (data.pptx_base64) {
          setPptxData({
            base64: data.pptx_base64,
            filename: data.filename
          });
        }
        
        setIsResultDialogOpen(true);
        fetchData(); // Refresh generations history
        fetchAIUsage(); // Update usage counter
        
        // Show usage info if available
        if (data.usage) {
          if (data.usage.is_trial) {
            toast.success('Content generated! That was your free trial - upgrade to get more AI generations!');
          } else {
            toast.success(`Content generated! ${data.usage.remaining} generations remaining this month.`);
          }
        } else {
          toast.success('Content generated successfully!');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate content');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent);
    toast.success('Copied to clipboard!');
  };

  const downloadAsText = () => {
    const blob = new Blob([generatedContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedType}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded!');
  };

  const downloadPptx = () => {
    if (!pptxData) return;
    
    // Convert base64 to blob
    const byteCharacters = atob(pptxData.base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = pptxData.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('PowerPoint downloaded!');
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: templateName,
          content: generatedContent,
          generation_type: selectedType,
          tags: [],
          color_scheme: 'default',
          images: []
        }),
      });

      if (response.ok) {
        toast.success('Template saved to your library!');
        setTemplateName('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const openBeautifulPreview = async () => {
    setIsLoadingPreview(true);
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/render-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: generatedContent,
          generation_type: selectedType,
          partner_id: selectedPartner || null
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setIsPreviewOpen(true);
      } else {
        toast.error('Failed to generate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const printPreview = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(previewHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const openPreviewInNewTab = () => {
    const newWindow = window.open('', '_blank');
    newWindow.document.write(previewHtml);
    newWindow.document.close();
  };

  // Show upgrade prompt only if completely out of generations
  const hasNoGenerationsLeft = !aiUsage.is_unlimited && !aiUsage.is_lifetime_pro && aiUsage.remaining <= 0 && aiUsage.limit > 0;
  const isFreeWithNoTrial = isFree && aiUsage.limit === 0 && aiUsage.remaining === 0;

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Check if organization profile is set up
  if (!organization || !organization.name || !organization.mission) {
    return (
      <div className="min-h-screen pt-24 pb-16" data-testid="ai-generator-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-orange-500" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#0A0A0A] mb-4">
            Set Up Your Organization First
          </h1>
          <p className="text-lg text-[#5C6B67] mb-8 max-w-xl mx-auto">
            To generate personalized content, we need to know about your nonprofit. 
            Please complete your organization profile.
          </p>
          <Button 
            onClick={() => navigate('/organization')}
            className="bg-primary hover:bg-primary-800 text-white"
          >
            Set Up Organization Profile
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAFAFA]" data-testid="ai-generator-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary-800 rounded-2xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#0A0A0A]">
                AI Content Generator
              </h1>
              <p className="text-[#5C6B67]">
                Create personalized proposals, emails, and grant write-ups
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* AI Usage Counter */}
            {aiUsage.is_lifetime_pro ? (
              <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1">
                Unlimited (Lifetime Pro)
              </Badge>
            ) : aiUsage.is_unlimited ? (
              <Badge className="bg-primary text-white px-3 py-1">
                Unlimited AI
              </Badge>
            ) : (
              <div className="flex items-center gap-2 bg-stone-100 px-4 py-2 rounded-lg">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-stone-700">
                  {aiUsage.remaining} / {aiUsage.limit} generations left
                </span>
                {aiUsage.remaining <= 5 && aiUsage.remaining > 0 && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">Low</Badge>
                )}
                {aiUsage.remaining === 0 && (
                  <Badge variant="outline" className="text-red-600 border-red-300 text-xs">Empty</Badge>
                )}
              </div>
            )}
            
            <Button 
              variant="outline" 
              onClick={() => setIsHistoryDialogOpen(true)}
              className="border-primary text-primary hover:bg-primary-50"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
          </div>
        </div>

        {/* Upgrade prompt for users at/near limits */}
        {(hasNoGenerationsLeft || isFreeWithNoTrial) && (
          <div className="bg-gradient-to-r from-primary/5 to-amber-50 border border-primary/20 rounded-lg p-4 flex items-center justify-between mb-6" data-testid="ai-upgrade-banner">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-800">
                  {isFreeWithNoTrial ? "You've used your free trial generation" : "You've reached your monthly limit"}
                </p>
                <p className="text-xs text-[#5C6B67]">Upgrade for more proposal generations</p>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate('/pricing')} className="bg-primary hover:bg-primary-800 text-white flex-shrink-0" data-testid="ai-upgrade-btn">
              View Plans
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Content Type Selection */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white border-[#E2E8E6]">
              <CardHeader>
                <CardTitle>1. Select Content Type</CardTitle>
                <CardDescription>Choose what you want to generate</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {GENERATION_TYPES.map((type) => {
                    const TypeIcon = type.icon;
                    const isSelected = selectedType === type.id;
                    
                    return (
                      <button
                        key={type.id}
                        onClick={() => {
                          setSelectedType(type.id);
                          // Reset selections when type changes
                          if (!type.requiresPartner) setSelectedPartner('');
                          if (!type.requiresProgram) setSelectedProgram('');
                          if (!type.requiresGrant) setSelectedGrant('');
                        }}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary-50 shadow-md' 
                            : 'border-[#E2E8E6] hover:border-stone-300 hover:shadow-sm'
                        }`}
                        data-testid={`content-type-${type.id}`}
                      >
                        <div className={`w-10 h-10 ${type.color} rounded-lg flex items-center justify-center mb-3`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <h3 className="font-semibold text-stone-900 mb-1 text-sm">{type.name}</h3>
                        <p className="text-xs text-[#5C6B67] line-clamp-2">{type.description}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Configuration */}
            {selectedType && (
              <Card className="bg-white border-[#E2E8E6]">
                <CardHeader>
                  <CardTitle>2. Configure Generation</CardTitle>
                  <CardDescription>Select the data sources for your content</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {GENERATION_TYPES.find(t => t.id === selectedType)?.requiresPartner && (
                    <div>
                      <Label>Select Partner/Company *</Label>
                      {partners.length === 0 ? (
                        <div className="p-4 bg-[#FAFAFA] rounded-lg mt-2">
                          <p className="text-sm text-[#5C6B67] mb-2">
                            No partners added yet. Add a partner to generate personalized content.
                          </p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate('/partners')}
                          >
                            Add Partner
                          </Button>
                        </div>
                      ) : (
                        <select
                          value={selectedPartner}
                          onChange={(e) => setSelectedPartner(e.target.value)}
                          className="w-full h-10 px-3 border border-[#E2E8E6] rounded-md mt-1"
                          data-testid="partner-select"
                        >
                          <option value="">Select a partner...</option>
                          {partners.map(partner => (
                            <option key={partner.id} value={partner.id}>
                              {partner.name} {partner.scraped_data && !partner.scraped_data.error ? '(Website Analyzed)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {GENERATION_TYPES.find(t => t.id === selectedType)?.requiresProgram && (
                    <div>
                      <Label>Select Program *</Label>
                      {programs.length === 0 ? (
                        <div className="p-4 bg-[#FAFAFA] rounded-lg mt-2">
                          <p className="text-sm text-[#5C6B67] mb-2">
                            No programs added yet. Add a program to generate grant content.
                          </p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate('/programs')}
                          >
                            Add Program
                          </Button>
                        </div>
                      ) : (
                        <select
                          value={selectedProgram}
                          onChange={(e) => setSelectedProgram(e.target.value)}
                          className="w-full h-10 px-3 border border-[#E2E8E6] rounded-md mt-1"
                          data-testid="program-select"
                        >
                          <option value="">Select a program...</option>
                          {programs.map(program => (
                            <option key={program.id} value={program.id}>{program.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {GENERATION_TYPES.find(t => t.id === selectedType)?.requiresGrant && (
                    <div>
                      <Label>Select Grant *</Label>
                      {grants.length === 0 ? (
                        <div className="p-4 bg-[#FAFAFA] rounded-lg mt-2">
                          <p className="text-sm text-[#5C6B67] mb-2">
                            No grants added yet. Add a grant to generate grant proposals.
                          </p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate('/grants')}
                          >
                            Add Grant
                          </Button>
                        </div>
                      ) : (
                        <select
                          value={selectedGrant}
                          onChange={(e) => setSelectedGrant(e.target.value)}
                          className="w-full h-10 px-3 border border-[#E2E8E6] rounded-md mt-1"
                          data-testid="grant-select"
                        >
                          <option value="">Select a grant...</option>
                          {grants.map(grant => (
                            <option key={grant.id} value={grant.id}>
                              {grant.name} - {grant.funder_name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tone</Label>
                      <select
                        value={tone}
                        onChange={(e) => setTone(e.target.value)}
                        className="w-full h-10 px-3 border border-[#E2E8E6] rounded-md mt-1"
                      >
                        <option value="professional">Professional</option>
                        <option value="friendly">Friendly</option>
                        <option value="persuasive">Persuasive</option>
                      </select>
                    </div>
                    <div>
                      <Label>Length</Label>
                      <select
                        value={length}
                        onChange={(e) => setLength(e.target.value)}
                        className="w-full h-10 px-3 border border-[#E2E8E6] rounded-md mt-1"
                      >
                        <option value="short">Short (~500 words)</option>
                        <option value="medium">Medium (~1000 words)</option>
                        <option value="long">Long (~2000 words)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label>Additional Context (Optional)</Label>
                    <Textarea
                      value={additionalContext}
                      onChange={(e) => setAdditionalContext(e.target.value)}
                      placeholder="Add any specific details, recent conversations, or context to personalize the content further..."
                      rows={3}
                      className="mt-1"
                    />
                  </div>

                  <Button 
                    onClick={handleGenerate}
                    className="w-full bg-primary hover:bg-primary-800 text-white h-12 text-lg"
                    disabled={generating}
                    data-testid="generate-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating with AI...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-5 h-5 mr-2" />
                        Generate Content
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Organization Preview */}
            <Card className="bg-white border-[#E2E8E6]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Your Organization</CardTitle>
              </CardHeader>
              <CardContent>
                <h3 className="font-semibold text-stone-900 mb-1">{organization.name}</h3>
                <p className="text-sm text-[#5C6B67] line-clamp-3">{organization.mission}</p>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-primary mt-2"
                  onClick={() => navigate('/organization')}
                >
                  Edit Profile
                </Button>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="bg-white border-[#E2E8E6]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Your Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/partners" className="flex items-center justify-between p-2 rounded-lg hover:bg-[#FAFAFA]">
                  <span className="text-sm text-[#5C6B67]">Partners</span>
                  <Badge variant="secondary">{partners.length}</Badge>
                </Link>
                <Link to="/programs" className="flex items-center justify-between p-2 rounded-lg hover:bg-[#FAFAFA]">
                  <span className="text-sm text-[#5C6B67]">Programs</span>
                  <Badge variant="secondary">{programs.length}</Badge>
                </Link>
                <Link to="/grants" className="flex items-center justify-between p-2 rounded-lg hover:bg-[#FAFAFA]">
                  <span className="text-sm text-[#5C6B67]">Grants</span>
                  <Badge variant="secondary">{grants.length}</Badge>
                </Link>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="bg-primary-50 border-primary-100">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-[#0A0A0A] mb-2">Tips for Better Results</h3>
                <ul className="text-sm text-primary-800 space-y-2">
                  <li>Add partner website URLs and click "Analyze Website" for personalization</li>
                  <li>Complete program details including outcomes and metrics</li>
                  <li>Add grant requirements for accurate proposals</li>
                  <li>Use additional context for specific talking points</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Result Dialog */}
        <Dialog open={isResultDialogOpen} onOpenChange={setIsResultDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Generated Content
                {pptxData && (
                  <Badge className="bg-indigo-100 text-indigo-700 ml-2">
                    PowerPoint Ready
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {/* Action Bar */}
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg border border-primary-100">
              <div className="flex items-center gap-2 flex-1">
                <Save className="w-5 h-5 text-primary shrink-0" />
                <Input
                  placeholder="Template name..."
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="bg-white max-w-[200px]"
                />
                <Button 
                  onClick={saveAsTemplate}
                  disabled={isSaving || !templateName.trim()}
                  size="sm"
                  className="bg-primary hover:bg-primary-800 text-white shrink-0"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
              <div className="border-l border-[#E2E8E6] h-8" />
              <Button 
                onClick={openBeautifulPreview}
                disabled={isLoadingPreview}
                variant="outline"
                size="sm"
                className="border-secondary text-secondary hover:bg-secondary-50"
              >
                {isLoadingPreview ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Eye className="w-4 h-4 mr-1" />
                )}
                Beautiful Preview
              </Button>
              <Button
                onClick={() => {
                  setIsResultDialogOpen(false);
                  navigate('/design-studio', {
                    state: {
                      importedContent: generatedContent,
                      contentType: selectedType,
                      contentTitle: GENERATION_TYPES.find(t => t.id === selectedType)?.name || 'AI Generated Content'
                    }
                  });
                }}
                variant="outline"
                size="sm"
                className="border-primary text-primary hover:bg-primary-50"
                data-testid="edit-in-design-studio-btn"
              >
                <Palette className="w-4 h-4 mr-1" />
                Edit in Design Studio
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto mt-4 bg-[#FAFAFA] rounded-lg p-6">
              <pre className="whitespace-pre-wrap text-sm text-stone-800 font-sans leading-relaxed">
                {generatedContent}
              </pre>
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-xs text-[#5C6B67]">
                Click "Beautiful Preview" for a print-ready formatted document
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyToClipboard}>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </Button>
                <Button variant="outline" size="sm" onClick={downloadAsText}>
                  <Download className="w-4 h-4 mr-1" />
                  Text
                </Button>
                {pptxData && (
                  <Button 
                    onClick={downloadPptx}
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    <Presentation className="w-4 h-4 mr-1" />
                    PowerPoint
                  </Button>
                )}
                <Button onClick={() => setIsResultDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Generation History
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto mt-4 space-y-3">
              {generations.length === 0 ? (
                <p className="text-center text-[#5C6B67] py-8">No generations yet</p>
              ) : (
                generations.map((gen) => {
                  const typeConfig = GENERATION_TYPES.find(t => t.id === gen.generation_type);
                  const TypeIcon = typeConfig?.icon || FileText;
                  
                  return (
                    <button
                      key={gen.id}
                      onClick={() => {
                        setGeneratedContent(gen.content);
                        setIsHistoryDialogOpen(false);
                        setIsResultDialogOpen(true);
                      }}
                      className="w-full p-4 border border-[#E2E8E6] rounded-lg text-left hover:bg-[#FAFAFA] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${typeConfig?.color || 'bg-stone-100'} rounded-lg flex items-center justify-center`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-stone-900">{typeConfig?.name || gen.generation_type}</h4>
                          <p className="text-sm text-[#5C6B67]">
                            {new Date(gen.created_at).toLocaleDateString()} at {new Date(gen.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-stone-400" />
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Beautiful Preview Dialog */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-[#FAFAFA]">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                Beautiful Preview
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={openPreviewInNewTab}
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Open in Tab
                </Button>
                <Button 
                  onClick={printPreview}
                  size="sm"
                  className="bg-primary hover:bg-primary-800 text-white"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Print / PDF
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-stone-200 p-6">
              <div 
                className="bg-white shadow-xl mx-auto"
                style={{ maxWidth: '8.5in' }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
